import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { fetchYardiApplicantsList } from '../services/yardiInviteService'
import type { YardiApplicantRow, YardiApplicantDetailPanelProps } from '../types'
import { formatDisplayName, maskformatPhoneNumber } from '../utils/yardiInviteUi'
import { formatInviteSentOn } from '../utils/yardiInviteFormatters'
import { APPLICANT_COL_WIDTHS_PCT } from '../constants'
import { showError, formatApiError } from '@/lib/utils/notifications'
import yardiTableStyles from '../styles/yardiListing.module.css'

export function YardiApplicantDetailPanel({ applicationId }: YardiApplicantDetailPanelProps) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<YardiApplicantRow[] | null>(null)
  const appSuffix = String(applicationId)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const data = await fetchYardiApplicantsList(applicationId)
        if (!cancelled) setRows(data)
      } catch (e) {
        showError(formatApiError(e), false)
        if (!cancelled) setRows([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [applicationId])

  if (loading) {
    return (
      <div
        className="flex w-full min-h-[4rem] items-center justify-center gap-2 py-6 text-slate-600"
        data-testid={`yardi-invite-applicant-panel-loading-${appSuffix}`}
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span>Loading</span>
      </div>
    )
  }

  if (!rows?.length) {
    return (
      <div className="w-full py-3 text-left" data-testid={`yardi-invite-applicant-panel-empty-${appSuffix}`}>
        <h6
          className="text-sm font-semibold text-slate-800"
          data-testid={`yardi-invite-applicant-panel-empty-heading-${appSuffix}`}
        >
          No applicants found
        </h6>
      </div>
    )
  }

  return (
    <div
      className="w-full pl-3 py-2 text-left"
      data-testid={`yardi-invite-applicant-panel-${appSuffix}`}
    >
      <h6
        className="mb-2 text-sm font-semibold text-slate-800"
        data-testid={`yardi-invite-applicant-panel-heading-${appSuffix}`}
      >
        Occupants
      </h6>
      <div
        className="overflow-x-auto rounded-lg border border-slate-200"
        data-testid={`yardi-invite-applicant-panel-table-wrap-${appSuffix}`}
      >
        <table
          className={`${yardiTableStyles.yardiApplicantTable} text-left text-sm text-slate-700`}
          data-testid={`yardi-invite-applicant-panel-table-${appSuffix}`}
        >
          <colgroup>
            {APPLICANT_COL_WIDTHS_PCT.map((pct, i) => (
              <col key={i} style={{ width: `${pct}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr data-testid={`yardi-invite-applicant-panel-table-head-${appSuffix}`}>
              <th
                data-testid={`yardi-invite-applicant-panel-col-prospect-id-${appSuffix}`}
                scope="col"
              >
                <span className="inline-block leading-tight">Prospect ID</span>
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-name-${appSuffix}`}
                scope="col"
              >
                Name
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-email-${appSuffix}`}
                scope="col"
              >
                Email
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-phone-${appSuffix}`}
                scope="col"
              >
                Phone Number
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-relationship-${appSuffix}`}
                scope="col"
              >
                <span className="inline-block leading-tight">Relationship Type</span>
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-invite-sent-${appSuffix}`}
                scope="col"
              >
                Invite Sent on
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-email-status-${appSuffix}`}
                scope="col"
              >
                <span className="inline-block leading-tight">Email Status</span>
              </th>
              <th
                data-testid={`yardi-invite-applicant-panel-col-reason-${appSuffix}`}
                scope="col"
              >
                Reason
              </th>
            </tr>
          </thead>
          <tbody data-testid={`yardi-invite-applicant-panel-table-body-${appSuffix}`}>
            {rows.map((applicant, i) => (
              <tr
                key={i}
                className="border-t border-slate-100"
                data-testid={`yardi-invite-applicant-panel-row-${appSuffix}-${i}`}
              >
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-prospect-id-${appSuffix}-${i}`}
                >
                  {applicant.prospect_id}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-name-${appSuffix}-${i}`}
                >
                  {formatDisplayName(
                    `${applicant.first_name ?? ''} ${applicant.last_name ?? ''}`.trim(),
                  )}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-email-${appSuffix}-${i}`}
                >
                  {applicant.email}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-phone-${appSuffix}-${i}`}
                >
                  {applicant.mobile_no ? maskformatPhoneNumber(String(applicant.mobile_no)) : ''}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-relationship-${appSuffix}-${i}`}
                >
                  {applicant.relationship_type}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-invite-sent-${appSuffix}-${i}`}
                >
                  {formatInviteSentOn(applicant.invite_sent_on)}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-email-status-${appSuffix}-${i}`}
                >
                  {applicant.email_status}
                </td>
                <td
                  data-testid={`yardi-invite-applicant-panel-cell-reason-${appSuffix}-${i}`}
                >
                  {applicant.reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
